import React, { memo, useState } from 'react';
import PropTypes from 'prop-types';
import { useDebouncedCallback } from 'use-debounce';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Field from './Field';
import Constants from '../../utils/Constants';
import './_styles_/SelectField.css';
import { extendedStateType } from '../../types';
import { resolveValue } from '../../utils/fields';
import { NO_STATE } from '../../hooks/useExtendedState';
import useEffectOnUpdate from '../../hooks/useEffectOnUpdate';

SelectField.propTypes = {
    value: PropTypes.string,
    label: PropTypes.node,
    inputId: PropTypes.string,
    onChange: PropTypes.func,
    validator: PropTypes.func,
    onValidated: PropTypes.func,
    className: PropTypes.string,
    postfix: PropTypes.node,
    emptyOption: PropTypes.node,
    options: PropTypes.node,
    onReset: PropTypes.func,
    isChanged: PropTypes.bool,
    disabled: PropTypes.bool,
    state: extendedStateType,
    valueTransformer: PropTypes.func,
};

SelectField.defaultProps = {
    onChange: undefined,
    validator: null,
    onValidated: undefined,
    label: null,
    inputId: null,
    className: '',
    postfix: null,
    emptyOption: null,
    options: null,
    onReset: undefined,
    isChanged: undefined,
    disabled: false,
    value: undefined,
    state: NO_STATE,
    valueTransformer: undefined,
};

function SelectField(props) {
    const {
        label,
        inputId,
        value,
        onChange,
        className,
        validator,
        onValidated,
        postfix,
        emptyOption,
        options,
        onReset,
        isChanged,
        disabled,
        state,
        valueTransformer,
    } = props;
    const resolvedValue = resolveValue(value, state);
    const [displayedValue, setDisplayedValue] = useState(resolvedValue);
    const [error, setError] = useState(null);
    const stateValueSetter = state && state !== NO_STATE ? state.setValueIfNew : () => {};
    const debouncedSetError = useDebouncedCallback((newError) => setError(newError), Constants.DISPLAY_FIELD_ERROR_DELAY_IN_MS);
    const validation = (newValue) => {
        validator && validator(newValue)
            .then(() => onValidationComplete(null, newValue))
            .catch((e) => onValidationComplete(e, newValue));
    };
    const onValidationComplete = (newError, newValue) => {
        onValidated && onValidated(newValue, validator === null ? null : !newError);
        debouncedSetError(newError);
    };
    const setNewValue = (newValue) => {
        if (newValue !== displayedValue) {
            setDisplayedValue(newValue);
            const resolvedNewValue = valueTransformer ? valueTransformer(newValue) : newValue;
            stateValueSetter(resolvedNewValue);
            onChange && onChange(resolvedNewValue);

            if (validator) {
                validation(resolvedNewValue);
            } else {
                onValidationComplete(null, resolvedNewValue);
            }
        }
    };
    useEffectOnUpdate(() => {
        setNewValue(resolveValue(value, state));
    }, [value, state.value]);
    const onChangeHandler = (event) => setNewValue(event.target.value);

    return (
        <Field
            label={label}
            postfix={postfix}
            inputId={inputId}
            error={error}
            onReset={onReset}
            isChanged={isChanged}
            state={state}
            disabled={disabled}
        >
            <div className="select-wrapper">
                <select className={className} value={displayedValue} onChange={onChangeHandler} disabled={disabled}>
                    {emptyOption}
                    {options}
                </select>
                <FontAwesomeIcon icon="angle-down" className="select-wrapper--caret" />
            </div>
        </Field>
    );
}

export default memo(SelectField);
